<template>
  <div class="jurisdiction-dialog-wrapper">
    <CustomDialog
      ref="customDialog"
      :title="_title"
      :subtitle="_subtitle"
      :footerButtons="_buttons"
      :loading="loading"
      @submit="submit()"
      @opened="showForm = true"
      @closed="showForm = false"
    >
      <div>
        <v-form
          v-if="showForm"
          v-model="valid"
          ref="form"
          class="mt-4"
          :disabled="loading"
        >
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                v-model="form.name"
                :label="`Nome do ${this.$t('group')}`"
                :disabled="_viewMode"
                :rules="_rules"
                outlined
              />
            </v-col>

            <v-col cols="12">
              <ItemSelector
                v-model="sections.people.selected"
                :items="sections.people.data"
                :loading="loading"
                :multiple="true"
                :disabled="_viewMode"
                :required="false"
                :item-value="'_id'"
                :item-text="'name'"
                :item-sub-text="'email'"
                :placeholder="sections.people.placeholder"
                :label="sections.people.label"
                :count="sections.people.count"
                @input="handleSelectedPeople"
                @search="handleSearchPeople"
                @paginate="fetchMorePeople"
              />
            </v-col>

            <v-col cols="12" md="12" class="mt-4">
              <v-select
                v-model="form.enableRequesterChat"
                :items="_requesterChatOptions"
                :label="$t('requesterChat.label')"
                :hint="$t('requesterChat.hintGroup')"
                :disabled="_viewMode"
                outlined
              />
            </v-col>
          </v-row>
        </v-form>

        <CustomDialog
          ref="confirmDialog"
          title="Confirmação de alteração"
          subtitle="Deseja realmente salvar estas alterações?"
          width="800"
          :footerButtons="_confirmButtons"
          @submit="submitConfirmation(true)"
          @close="submitConfirmation(false)"
        >
          <div>
            <p v-if="addedUsers.length">
              • Adição dos Administradores: {{ addedUsers.join(", ") }}
            </p>

            <p v-if="removedUsers.length">
              • Remoção dos Administradores: {{ removedUsers.join(", ") }}
            </p>
          </div>
        </CustomDialog>
      </div>
    </CustomDialog>

    <loader-hover v-model="loading" />
  </div>
</template>

<script>
import {
  createJurisdictions,
  updateJurisdictions,
} from "@/services/jurisdictions";
import { getUsers } from "@/services/users";
import { getPermissionGroups } from "@/services/permissions";
import { handleAlert } from "@/utils";
import { validation } from "@/utils/validators";
import CustomDialog from "@/components/customDialog/CustomDialog.vue";
import ItemSelector from "@/components/itemSelector/ItemSelector.vue";
import { requesterChatOptions } from "@/utils/requesterChat";

export default {
  name: "JurisdictionDialog",

  components: {
    CustomDialog,
    ItemSelector,
  },

  data() {
    return {
      showForm: false,
      mode: "create",
      valid: false,
      loading: false,
      adminGroupID: "",
      form: {
        _id: "",
        name: "",
        peopleIDs: [],
        enableRequesterChat: null,
      },
      sections: {
        people: {
          label: "Administradores",
          placeholder: "Digite para buscar...",
          loading: true,
          data: [],
          selected: [],
          page: 0,
          count: 0,
        },
      },
      attendants: [],
      addedUsers: [],
      removedUsers: [],
    };
  },

  watch: {
    ["sections.people.selected"]: {
      handler(val) {
        const newIDs = val.map((user) => user._id);
        const originalIDs = this.attendants.map((user) => user._id);

        const added = val.filter((user) => !originalIDs.includes(user._id));
        const removed = this.attendants.filter(
          (user) => !newIDs.includes(user._id)
        );

        this.addedUsers = added.map((user) => user.name || user.email);
        this.removedUsers = removed.map((user) => user.name || user.email);
      },
      deep: true,
    },
  },

  computed: {
    _requesterChatOptions() {
      return requesterChatOptions({
        inherit: this.$t("requesterChat.notConfiguredHere"),
        allow: this.$t("requesterChat.allow"),
        block: this.$t("requesterChat.block"),
      });
    },

    _viewMode() {
      return this.mode === "view";
    },

    _editMode() {
      return this.mode === "edit";
    },

    _title() {
      if (this._viewMode) return `Visualizar ${this.$t("group")}`;
      else if (this._editMode) return `Editar ${this.$t("group")}`;

      return `Adicionar ${this.$t("group")}`;
    },

    _subtitle() {
      if (this._viewMode) return "";
      if (this._editMode)
        return `Preencha os campos abaixo para editar ${this.$t("group")}`;

      return `Preencha os campos abaixo para cadastrar ${this.$t("group")}`;
    },

    _confirmButtons() {
      return [
        {
          label: "Cancelar",
          color: "secondary",
          action: "close",
        },
        {
          label: "Confirmar",
          color: "primary",
          action: "submit",
        },
      ];
    },

    _buttons() {
      if (this._viewMode)
        return [
          {
            label: "Fechar",
            color: "primary",
            action: "close",
          },
        ];

      return [
        {
          label: "Cancelar",
          color: "secondary",
          action: "close",
        },
        {
          label: "Finalizar",
          color: "primary",
          action: "submit",
        },
      ];
    },

    _rules() {
      return [
        (v) => (!!v && v.length > 0) || "Campo obrigatório",
        (v) => validation(v) || "Informe um valor válido",
      ];
    },
  },

  methods: {
    async submit() {
      if (!this.$refs.form.validate()) return;

      if (this.addedUsers.length || this.removedUsers.length) {
        this.$refs.confirmDialog.open();
        return;
      }

      this.handleJurisdiction();
    },

    async handleJurisdiction() {
      this.loading = true;

      if (this._editMode) await this.updateJurisdictions();
      else await this.createJurisdictions();

      this.loading = false;
    },

    submitConfirmation(confirm = true) {
      this.$refs.confirmDialog.close();

      if (confirm) this.handleJurisdiction();
    },

    async createJurisdictions() {
      try {
        const payload = {
          type: this.form.type,
          name: this.form.name,
          peopleIDs: this.form.peopleIDs,
          // `null` = não configurado nesta comarca; vara e balcão decidem
          // sozinhos e, na falta de todos, vale o default do client (ADR-008).
          enableRequesterChat: this.form.enableRequesterChat,
        };

        await createJurisdictions(payload);

        this.handleAlert(`${this.$t("group")} criado com sucesso`, "success");
        this.$emit("success");

        this.closeDialog();
      } catch (error) {
        this.handleAlert(error.data.message, "error");
      }
    },

    async updateJurisdictions() {
      try {
        const payload = {
          name: this.form.name,
          peopleIDs: this.form.peopleIDs,
          enableRequesterChat: this.form.enableRequesterChat,
        };

        await updateJurisdictions(this.form._id, payload);

        this.handleAlert(`${this.$t("group")} editado com sucesso`, "success");
        this.$emit("success");

        this.closeDialog();
      } catch (error) {
        this.handleAlert(error.data.message, "error");
      }
    },

    async openDialog(data, mode) {
      this.resetForm();

      this.mode = mode || "create";
      this.loading = true;

      if (data) {
        const people = data?.people?.map((el) => ({
          _id: el._id,
          name: el.name,
          email: el.email,
        }));

        this.attendants = people;

        this.sections.people.page = 0;
        this.sections.people.count = 0;
        this.sections.people.loading = false;
        this.sections.people.data = JSON.parse(JSON.stringify(people || []));
        this.sections.people.selected = JSON.parse(
          JSON.stringify(people || [])
        );

        this.form = {
          _id: data._id,
          name: data.name,
          // Precisa ser `peopleIDs`: é o campo que o payload envia. Gravando em
          // `people`, o modal abria com as pessoas na tela mas salvava a lista
          // que o `resetForm` tinha zerado — qualquer edição, inclusive só a do
          // chat do solicitante, desassociava todo mundo.
          peopleIDs: (data?.people || []).map((el) => el._id),
          enableRequesterChat: data.enableRequesterChat ?? null,
        };
      }

      await this.getPermissionGroups();
      await Promise.all([this.handleFetchPeople()]);

      this.$refs.customDialog.open();
      this.loading = false;
    },

    closeDialog() {
      this.resetForm();
      this.$refs.customDialog.close();
    },

    async getPermissionGroups() {
      if (!this.$can("attendances")) return;

      try {
        const payload = {
          module: "attendances",
          type: "role",
        };

        const { data } = await getPermissionGroups(payload);

        this.adminGroupID = data.find((el) => el.role === "admin")?._id || "";
      } catch (error) {
        this.handleAlert(error.data.message, "error");
      }
    },

    resetForm() {
      this.administrators = [];

      this.loading = false;
      this.form._id = "";
      this.form.name = "";
      this.form.peopleIDs = [];
      this.form.enableRequesterChat = null;
      if (this.$refs.form) this.$refs.form.resetValidation();

      this.sections.people.selected = [];

      this.addedUsers = [];
      this.removedUsers = [];
      this.attendants = [];
    },

    async handleFetchPeople(search = "") {
      if (!this.adminGroupID) return;

      this.sections.people.loading = true;

      const payload = {
        sortBy: "email",
        sortOrder: "asc",
        limit: 10,
        offset: this.sections.people.page * 10,
        permissionGroupIDs: [this.adminGroupID],
        ...(!!search?.length && { search }),
        ...(!!this.sections.people.selected && {
          nin: this.sections.people.selected.map((el) => el._id), // Busca as pessoas com exclusão das selecionadas (nin = not in)
        }),
      };
      const {
        data,
        headers: { "x-count": count },
      } = await getUsers(payload);

      if (search) {
        this.sections.people.data = [
          ...this.sections.people.selected,
          ...data.map((person) => ({
            _id: person._id,
            name: person.name || person.email,
            email: person.email,
          })),
          ...this.sections.people.data,
        ];
      } else {
        this.sections.people.data = [
          ...this.sections.people.selected,
          ...this.sections.people.data,
          ...data.map((person) => ({
            _id: person._id,
            name: person.name || person.email,
            email: person.email,
          })),
        ];
      }

      this.sections.people.count = Number(count);
      this.sections.people.loading = false;
    },

    handleSelectedPeople(selected) {
      this.sections.people.selected = selected;
      this.form.peopleIDs = (selected || []).map((el) => el._id);
    },

    async handleSearchPeople(search = "") {
      this.sections.people.page = 0;
      this.sections.people.count = 0;
      this.sections.people.data = [];

      this.handleFetchPeople(search);
    },

    async fetchMorePeople(search = "") {
      this.sections.people.page++;
      this.handleFetchPeople(search);
    },

    handleAlert,
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
